import {useContext, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import {debounce} from '@s-ui/js/lib/function'
import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import Context from '@s-ui/react-context'
import SpinnerBasic, {spinnerBasicOverlayTypes, spinnerBasicTypes} from '@adv-mt/mt-spinner-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {BASE_CLASS, MODAL_CONTENT_IDS, PANEL_FINANCING_VERSION} from '../config.js'
import {PanelFinancingCta} from '../cta/PanelFinancingCta.js'
import {PanelFinancingInfo} from '../detailFinancingInfo/PanelFinancingInfo.js'
import {PanelFinancingInstalmentSection} from '../instalment/PanelFinancingInstalmentSection.js'
import {PanelFinancingLenderInfo} from '../lenderInfo/PanelFinancingLenderInfo.js'
import {PanelFinancingModal} from '../modal/PanelFinancingModal.js'

const DEBOUNCE_WAIT = 500
const FINANCING_TERMS_CONDITIONS_ID = 'mt-Detail-financingTerms'
const scrollParams = {behavior: 'smooth', block: 'center'}

export const PanelFinancingContentC2C = ({ad, isProfessional, financingData, onCalculatorChange, vehicleInfo = {}}) => {
  const {domain, i18n} = useContext(Context)

  const config = domain.get('config')

  const debounceOnChange = useRef()

  const {initialLoanCalculations, lender, terms} = financingData || {}
  const {legalContent} = MODAL_CONTENT_IDS

  const [loanCalculations, setLoanCalculations] = useState({
    ...financingData,
    ...initialLoanCalculations
  })

  const {minEntry, maxEntry} = loanCalculations || {}

  const [entryValue, setEntryValue] = useState(minEntry)
  const [selectedTerms, setSelectedTerms] = useState(terms)
  const [isLoadingCalculations, setIsLoadingCalculations] = useState(false)
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [entryErrorText, setEntryErrorText] = useState()
  const [hasInputError, setHasInputError] = useState(false)
  const [hasLoanUrlError, setHasLoanUrlError] = useState(false)
  const [hasErrorOnCalculation, setHasErrorOnCalculation] = useState(false)

  const {track} = useAnalyticsTrack()

  const {TRACKING_EVENT_CONTEXTS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const {id, make, model, plateRegistrationDate, price, version, year} = ad
  const {vehicleId} = vehicleInfo

  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const updateC2cLoanCalculations = async ({inputEntry, inputTerms}) => {
    setHasErrorOnCalculation(false)
    setIsLoadingCalculations(true)
    const [error, response] = await domain.get('get_loan_calculations_transactions_use_case').execute({
      carPlateRegistrationDate: plateRegistrationDate || `01/${year}`,
      lender,
      openingFee: inputEntry || entryValue,
      price,
      terms: inputTerms || selectedTerms
    })

    setIsLoadingCalculations(false)
    if (error) {
      onCalculatorChange({reset: true})
      setHasErrorOnCalculation(true)
      return
    }

    const updatedCalculations = {
      ...loanCalculations,
      ...response,
      entry: Number(inputEntry) || Number(entryValue),
      terms: inputTerms || selectedTerms
    }

    setLoanCalculations(updatedCalculations)
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad: {
        ...ad,
        financingData: updatedCalculations
      },
      event: TRACKING_EVENT_NAMES.FINANCING_CALCULATOR_LOAN_DATA_VISUALIZED
    })

    track(eventData)
    onCalculatorChange({financingData: updatedCalculations})
  }

  const validateEntry = value => {
    if (value < minEntry) {
      setEntryErrorText(i18n.t('ENTRY_MINIMUM_VALUE', {amount: i18n.c(minEntry)}))
      setHasInputError(true)
      onCalculatorChange({reset: true})
      setLoanCalculations({
        ...financingData,
        ...initialLoanCalculations
      })
      return
    }

    if (value > maxEntry) {
      setEntryErrorText(i18n.t('ENTRY_MAXIMUM_VALUE', {amount: i18n.c(maxEntry)}))
      setHasInputError(true)
      onCalculatorChange({reset: true})
      setLoanCalculations({
        ...financingData,
        ...initialLoanCalculations
      })
      return
    }

    setEntryErrorText()
    setHasInputError(false)
    return true
  }

  const handleEntryChange = value => {
    setHasLoanUrlError(false)
    setEntryValue(value)
    if (debounceOnChange.current) {
      debounceOnChange.current.cancel()
    }

    debounceOnChange.current = debounce(async () => {
      if (validateEntry(value)) {
        updateC2cLoanCalculations({inputEntry: value})
        const eventData = await domain.get('get_event_tracking_data_use_case').execute({
          ad: {...ad, selectedEntry: value},
          event: TRACKING_EVENT_NAMES.FINANCING_CALCULATOR_ENTRY_FEE_SELECTED
        })

        track(eventData)
      }
    }, DEBOUNCE_WAIT)

    debounceOnChange.current()
  }
  const handleTermsChange = value => {
    setHasLoanUrlError(false)
    setSelectedTerms(value)
    if (debounceOnChange.current) {
      debounceOnChange.current.cancel()
    }

    debounceOnChange.current = debounce(async () => {
      updateC2cLoanCalculations({inputTerms: value})
      const eventData = await domain.get('get_event_tracking_data_use_case').execute({
        ad: {...ad, selectedTerm: value},
        event: TRACKING_EVENT_NAMES.FINANCING_CALCULATOR_TERM_SELECTED
      })

      track(eventData)
    }, DEBOUNCE_WAIT)

    debounceOnChange.current()
  }

  const handleOnBlurEntry = () => {
    if (hasInputError) {
      setEntryValue(minEntry)
      updateC2cLoanCalculations({inputEntry: minEntry})
      setEntryErrorText()
      setHasInputError(false)
    }
  }

  const handleOnClickCtaLoan = async () => {
    setIsLoadingUrl(true)
    const [error, response] = await domain.get('get_loan_url_transactions_use_case').execute({
      ...loanCalculations,
      adId: id,
      entry: entryValue,
      make,
      model,
      plateRegistrationDate: plateRegistrationDate || `01/${year}`,
      price,
      terms: selectedTerms,
      version,
      vehicleId
    })

    const {url} = response || {}

    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad: {
        ...ad,
        financingData: {
          ...loanCalculations,
          entry: entryValue,
          terms: selectedTerms
        }
      },
      event: TRACKING_EVENT_NAMES.FINANCING_CALCULATOR_LOAN_APPLICATION_CLICKED
    })

    track(eventData)

    if (error) {
      setHasLoanUrlError(true)
      setIsLoadingUrl(false)
      return
    }

    setIsLoadingUrl(false)

    window && window.open(url)
  }

  const handleScrollToFinancingTerms = () => {
    const elementContactForm = document.getElementById(FINANCING_TERMS_CONDITIONS_ID)
    elementContactForm && elementContactForm.scrollIntoView(scrollParams)
  }

  const getLegalContentTrack = async () => {
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      ad,
      context: TRACKING_EVENT_CONTEXTS.FINANCING_CALCULATOR_CNET,
      event: TRACKING_EVENT_NAMES.FINANCING_DETAIL_LEGAL_CONDITIONS_CLICKED
    })

    track(eventData)
  }

  const handleOpenTermsAndConditionsModal = async () => {
    setIsModalOpen(!isModalOpen)
  }

  const handleOnClickCtaTerms = () => {
    getLegalContentTrack()
    if (isProfessional) handleScrollToFinancingTerms()
    else handleOpenTermsAndConditionsModal()
  }

  return (
    <>
      <div className={`${BASE_CLASS}-content`}>
        {(isLoadingCalculations || isLoadingUrl) && (
          <SpinnerBasic
            inheritColorText
            overlayType={spinnerBasicOverlayTypes.LIGHT}
            type={spinnerBasicTypes.SECTION}
            text={i18n.t('COMMON.PROCESS_CAN_TAKE_SECONDS')}
          />
        )}
        <PanelFinancingInstalmentSection
          isProfessional={isProfessional}
          entryErrorText={entryErrorText}
          entryValue={entryValue}
          handleEntryChange={handleEntryChange}
          handleOnBlurEntry={handleOnBlurEntry}
          handleTermsChange={handleTermsChange}
          hasInputError={hasInputError}
          loanCalculations={loanCalculations}
          selectedTerms={selectedTerms}
        />
        <PanelFinancingInfo
          ad={ad}
          isPrivate={!isProfessional}
          loanCalculations={loanCalculations}
          panelFinancingVersion={PANEL_FINANCING_VERSION.C2C}
        />
      </div>
      <PanelFinancingLenderInfo
        ad={ad}
        handleOnClickCtaTerms={handleOnClickCtaTerms}
        isPrivate={!isProfessional}
        loanCalculations={loanCalculations}
      />
      <div className={`${BASE_CLASS}-footer`}>
        <div className={`${BASE_CLASS}-footerError`}>
          {(hasLoanUrlError || hasErrorOnCalculation) && i18n.t('COMMON.SERVICE_DOWN_TRY_LATER')}
        </div>
        {!isProfessional && (
          <PanelFinancingCta
            ad={ad}
            buttonText={i18n.t('GET_PERSONAL_LOAN')}
            handleOnClick={handleOnClickCtaLoan}
            isDisabled={hasInputError || isLoadingUrl}
            loanCalculations={loanCalculations}
            entryValue={entryValue}
            selectedTerms={selectedTerms}
          />
        )}
      </div>
      {isModalOpen && !isProfessional && (
        <PanelFinancingModal
          // minEntry needs to be matched to user entry
          contentId={legalContent}
          financingData={{
            ...loanCalculations,
            ...(!hasInputError && {minEntry: entryValue})
          }}
          onModalClose={handleModalClose}
        />
      )}
    </>
  )
}

PanelFinancingContentC2C.propTypes = {
  ad: PropTypes.object,
  isProfessional: PropTypes.string,
  financingData: PropTypes.object,
  onCalculatorChange: PropTypes.func,
  vehicleInfo: PropTypes.object,
  pageName: PropTypes.string.isRequired
}
